import I18n from 'I18n';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { ENFORCE_FIELD_LEVEL_PERMISSIONS } from 'automation-ui-enrollment/constants/Gates';
import { GET_EVENT_TRIGGERS_EVENT_PROPERTIES } from 'automation-ui-enrollment/queries/FrameworkBuilderQueries';
import { GET_FLAT_OBJECT_PROPERTIES } from 'automation-ui-enrollment/queries/ObjectPropertiesQueries';
import { parseEventTypeId } from 'automation-ui-enrollment/utils/eventFilters';
import { DSFieldRecord } from 'customer-data-filters-ui/public/core';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import { useQuery } from 'data-fetching-client';
import enviro from 'enviro';
import { List } from 'immutable';
import { useEffect, useState } from 'react';
import { getFlpDisabledPropertyMessage } from 'automation-ui-core/utils/getFlpDisabledPropertyMessage';
// NOTE: this is a util made from desperation, do everything in your power to not add properties here
// const is wrapped in function because I18n cant be safely accessed at module evaluation time (eslinthubspot-dev/no-unsafe-i18n-at-module-eval)
export const getViewOnlyProperties = () => {
  return {
    /*
    hs_email_meta_content_id: old "Delay until event" event events with XOFilterEditor displayed "Email name"s
    hs_email_campaign_group_id: What other usages of SegmentationFilters use for "Email name"s
     We can't migrate hs_email_meta_content_id => hs_email_campaign_group_id because LIST_SEGGABLE_EMAILTYPES can't be be migrated
    from EMAIL_API_CAMPAIGN_GROUP/hs_email_campaign_group_id to EMAIL_API_CAMPAIGN/hs_email_meta_content_id, as they're different ids for different purposes
    Decided the easiest solution was to display the old property, while only allowing users to create the new property
     LIST_SEGGABLE_EMAILTYPES: https://sourcegraph.hubteam.com/git.hubteam.com/HubSpot/content4j/-/blob/ContentModel/src/main/java/com/hubspot/content/model/EmailType.java?L69-L85
    discussion: https://docs.google.com/document/d/1IpvhWEwh92WyHesQxP0nxade3P9MjBwe-53Bir0n4tA/edit#bookmark=id.n7bobjlmxwxy */
    hs_email_meta_content_id: DSFieldRecord({
      label: I18n.text('automation-ui-enrollment.redesign.events.viewOnlyPropertyLabels.hs_email_meta_content_id'),
      type: 'enumeration',
      name: 'hs_email_meta_content_id',
      referencedObjectType: 'EMAIL'
    })
  };
};

/* Provides the properties in SegmentationFilters' expected format (DSFieldRecord) for
every event type used in the filter viewer and the filter editor panel */
const useGetEventPropertyFieldsMap = triggers => {
  const enforceFlp = useIsUngated(ENFORCE_FIELD_LEVEL_PERMISSIONS);
  const [initialized, setInitialized] = useState(false);
  const [eventPropertyFieldsMap, setEventPropertyFieldsMap] = useState({});
  const eventTypeIdsToFetch = getEventTypeIdsToFetch(triggers);
  const [eventTypeIdToFetch, setEventTypeIdToFetch] = useState(eventTypeIdsToFetch.find(eventTypeId => !eventPropertyFieldsMap[eventTypeId]));

  /* there's no bulk event properties endpoint so we need to fetch each individual eventTypeId
  until all of the eventTypeIdsToFetch have an entry in the map */
  const {
    data: eventTriggersPropertiesData,
    loading: eventTriggersPropertiesLoading,
    error: eventTriggersPropertiesError
  } = useQuery(GET_EVENT_TRIGGERS_EVENT_PROPERTIES, {
    variables: {
      eventFQNOrObjectTypeId: eventTypeIdToFetch
    },
    skip: !eventTypeIdToFetch
  });
  const {
    data: originalPropertiesData,
    loading: originalPropertiesLoading,
    error: originalPropertiesError
  } = useQuery(GET_FLAT_OBJECT_PROPERTIES, {
    variables: {
      objectTypeId: eventTypeIdToFetch
    },
    skip: !eventTypeIdToFetch
  });

  /* TEMP: filter viewer can only render when property data is "initialized"
  if the property data isn't loaded when the filter viewer renders, SegmentationFilters displays a validation error
  that doesn't go away once the property is loaded */
  useEffect(() => {
    if (!eventTypeIdToFetch && !initialized) {
      setInitialized(true);
    }
  }, [eventTypeIdToFetch, initialized]);
  useEffect(() => {
    if (!eventTypeIdToFetch || eventPropertyFieldsMap[eventTypeIdToFetch]) {
      // fetches next eventTypeId until all eventTypeIdsToFetch are in eventTriggersPropertiesData
      const nextEventTypeIdToFetch = eventTypeIdsToFetch.find(eventTypeId => !eventPropertyFieldsMap[eventTypeId]);
      setEventTypeIdToFetch(nextEventTypeIdToFetch);
    }
  }, [eventTypeIdsToFetch, eventTypeIdToFetch, eventPropertyFieldsMap]);
  useEffect(() => {
    if (eventTriggersPropertiesError || originalPropertiesError) {
      // display unresolved properties and let filter editor show error message
      setEventPropertyFieldsMap(Object.assign({}, eventPropertyFieldsMap, {
        [eventTypeIdToFetch]: {}
      }));
    } else if (eventTypeIdToFetch && !eventPropertyFieldsMap[eventTypeIdToFetch] && !originalPropertiesLoading && !eventTriggersPropertiesLoading) {
      // sets the event type's entry in eventPropertyFieldsMap
      const originalProperties = originalPropertiesData ? originalPropertiesData.flatProperties.reduce((currentOriginalPropertiesData, propertyDefinition) => {
        currentOriginalPropertiesData[propertyDefinition.property.name] = propertyDefinition;
        return currentOriginalPropertiesData;
      }, {}) : {};
      const dsFieldRecord = propertiesToDSFieldRecord(eventTriggersPropertiesData.enrollment_eventTriggersEventProperties, originalProperties, enforceFlp);
      setEventPropertyFieldsMap(Object.assign({}, eventPropertyFieldsMap, {
        [eventTypeIdToFetch]: dsFieldRecord
      }));
    }
  }, [enforceFlp, eventPropertyFieldsMap, eventTriggersPropertiesData, eventTriggersPropertiesError, eventTriggersPropertiesLoading, eventTypeIdsToFetch, eventTypeIdToFetch, originalPropertiesData, originalPropertiesError, originalPropertiesLoading]);
  return {
    eventPropertyFieldsMap,
    initialized
  };
};

// https://tools.hubteamqa.com/puma/events?search=e_property_value_changed
function getIsPropertyChangedEvent(propertyTypeId) {
  return enviro.isQa() ? propertyTypeId === '4-67284' : propertyTypeId === '4-655002';
}
function getEventTypeIdsToFetch(triggers) {
  if (!triggers) {
    return [];
  }
  return triggers.filterBranches.map(filterBranch => {
    if (!filterBranch.filterBranches.length) {
      // list membership filters aren't real events and have no eventTypeId
      return undefined;
    }
    const eventFilterBranch = filterBranch.filterBranches[0];
    return eventFilterBranch.eventTypeId;
  }).filter(eventTypeId => !!eventTypeId).filter(eventTypeId => {
    const isHubSpotEvent = parseEventTypeId(eventTypeId).metaType === 4;
    // has different UI that's controlled by filtes FE
    const isPropertyValueChangedEvent = getIsPropertyChangedEvent(eventTypeId);
    return isHubSpotEvent && !isPropertyValueChangedEvent;
  });
}
function propertiesToDSFieldRecord(propertyMetadataRecord, originalProperties, enforceFlp) {
  return Object.entries(propertyMetadataRecord).reduce((acc, [key, property]) => {
    var _originalProperty$per;
    // originalProperties[key] should always be defined, but there are some cases in QA where it returns undefined
    const originalProperty = originalProperties[key];
    const options = (originalProperty === null || originalProperty === void 0 ? void 0 : originalProperty.property.options) || [];

    // https://product.hubteam.com/docs/hubspot-framework/permissions/field-level-permissions.html#deduced-permission-values
    const accessLevel = originalProperty === null || originalProperty === void 0 || (_originalProperty$per = originalProperty.permission) === null || _originalProperty$per === void 0 ? void 0 : _originalProperty$per.accessLevel;
    const disabled = enforceFlp && accessLevel !== 'not_specified' && accessLevel !== '';
    const tooltip = getFlpDisabledPropertyMessage(originalProperty === null || originalProperty === void 0 ? void 0 : originalProperty.property.name);
    const noAccessProps = {
      disabled: true,
      tooltip
    };
    acc[key] = DSFieldRecord(Object.assign({}, disabled ? noAccessProps : {}, {
      label: property.label || key,
      // a referenceType will only be used for enums
      type: property.referenceType ? 'enumeration' : property.type,
      name: key,
      // can't tell from referenceType if it should be external or not
      referencedObjectType: property.referenceType,
      externalOptionsReferenceType: property.externalOptionsReferenceType,
      // currently framework app settings can't support fetching options, which we need for some enum properties
      options: List(options.map(PropertyOptionRecord))
    }));
    return acc;
  }, {});
}
export default useGetEventPropertyFieldsMap;